import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

import AgentIcon from '../../../assets/images/icons/AgentIcon';

import EscalationUserIcon from '@cp/src/assets/icons/EscalationUserIcon';
import FeedbackIcon from 'src/assets/icons/FeedbackIcon';
import LogoutModal from '../LogoutModal';
import NaviNewLogoIcon from '@navi/web-ui/lib/icons/NaviLogoIcon/NaviNewLogoIcon';

import { AGENT_AVAILABILITY } from '@cp/src/pages/DashboardV2/dc-97/dc97Constant';
import {
  AGENT_STATUS,
  CURRENT_CHAT_REF_ID_KEY,
  HRC_CHAT_AGENT_ONLINE_STATUS
} from '../../../pages/WhatsappChatbot/types';
import { AMEYO_SESSION_EVENTS, HRC_CLICKSTREAM } from '../../../service/clickStream.constant';
import { AMEYO_STATUS_CODES } from 'src/service/naviExtension.service';
import {
  CosmosSyncBlockStatus,
  resetHumanReminderCustomerDetails,
  setAuthData,
  setShowLogoutModal
} from '../../../reducers/commonSlice';
import { DOCUMENTS_MAP } from '../constants/constants';
import { ExtensionHandler } from 'src/utils/extension.utils';
import { feedbackFormUrl } from 'src/constants/Global';
import { getAgentDocuments } from '../actions/downloadDRA';
import { getReporteesWithAllocation } from '../actions/escalationsAction';
import { LOCAL_STORAGE_KEYS } from '../../../constants/StorageKeys';
import { logOut } from 'src/pages/auth/AuthActions';
import { refreshCallData, setManualLoginCreds } from 'src/reducers/humanReminderSlice';
import { resetState } from '@cp/reducers/ameyoSlice';
import { RootState } from '../../../store';
import { Roles } from 'src/pages/auth/constants/AuthConstants';
import { setAgentAvailability } from '@cp/src/pages/DashboardV2/dc-97/HumanReminderAction';
import { setShowOTPScreen } from '../../../pages/auth/reducers/authSlice';
import { toast } from '@navi/web-ui/lib/primitives/Toast/core/toast';
import { agentOnlineToggleHandler } from '../../../pages/WhatsappChatbot/actions';
import { addClickstreamEvent } from '../../../service/clickStreamEventService';
import {
  setRecommendedCases,
  setCurrentRecommendedCase
} from '../../../pages/CaseRecommendation/reducer';

import { AmeyoCollapsibleToolbarV4 } from '@cp/components/Ameyo/AmeyoCollapsibleToolbarV4';
import OnlineOfflineSwitch from '@cp/src/components/sidebar/OnlineOfflineSwitch';
import SideBarItems from './SideBarItems';
import Switch from '@navi/web-ui/lib/primitives/Switch/Switch';
import Typography from '@navi/web-ui/lib/primitives/Typography/index';
import isNaviAgency from '@cp/src/utils/isNaviAgency';
import sdkHelper from '@cp/src/pages/DashboardV3/utils/sdkHelper';
import styles from './index.module.scss';
import useLocalStorageObserver from '../../../hooks/useLocalStorageObserver';
import { useAmeyoIntegration } from '@cp/src/hooks/useAmeyoIntegration';
import TooltipCircularProgress from '../CircularTooltip/TooltipCircularProgress';
import { clearParamsMap } from './SidebarLinks';
import NavigationLinks from '../NavigationLinks';
import APP_ROUTES from '@cp/src/layout/Routes';
import { clearImageCache } from '@cp/src/utils/WhatsappSelfieDB';
import { logError } from '@cp/src/utils/ApiHelper';
import { IT_TEAM_ROLES_COUNT, requiredRolesForItTeam } from '@cp/src/constants/Common.constants';

const SideNavigationPanel = () => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const [disableLogout, setDisableLogout] = useState<boolean>(false);

  const user = useSelector((state: RootState) => state.common.userData);
  const userData = useSelector((state: RootState) => state?.common?.userData);
  const roles = useSelector((store: RootState) => store?.common?.userData?.roles);
  const agentId = userData?.referenceId;
  const agencyCode = useSelector((state: RootState) => state?.common?.userData?.agencyCode);
  const agentReferenceId = useSelector((state: RootState) => state?.common?.userData?.referenceId);
  const rolesCount = user?.roles?.length;

  const isGlobalAccessGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const isOnlyGlobalAccessUser = isGlobalAccessGiven && rolesCount === 1;
  const isDc97User =
    (roles?.includes(Roles.ROLE_HUMAN_REMINDER_AGENT) ||
      roles?.includes(Roles.ROLE_HUMAN_REMINDER_SPADE_CALLING_AGENT)) &&
    !roles.includes(Roles.ROLE_HUMAN_REMINDER_CHAT_AGENT);
  const isHRCChatUser = roles?.includes(Roles.ROLE_HUMAN_REMINDER_CHAT_AGENT);

  const featureFlags = useSelector((state: RootState) => state?.common?.featureFlags);

  const breakTimeFeatureFlag = useSelector(
    (state: RootState) => state?.common?.featureFlags?.breakTimeFeatureFlag
  );

  const countOfEscalationEnabled = useSelector(
    (state: RootState) => state?.common?.featureFlags?.countOfEscalationEnabled
  );

  const isPerformanceDashboardVisible = useSelector(
    (state: RootState) => state?.common?.isPerformanceDashboardVisible
  );

  const isTeamLead = useSelector((state: RootState) => state?.common?.isTeamLead);

  const showLogoutModal = useSelector((state: RootState) => state?.common?.showLogoutModal);
  const isAgentOnline = useSelector((state: RootState) => state.humanReminder.isAgentOnline);
  const isAgentOfflineInLonghorn = useSelector((state: RootState) => !state?.common?.isAgentOnline);
  const isLonghornBlocked =
    useSelector((store: RootState) => store.common.cosmosSyncBlockStatus) ===
    CosmosSyncBlockStatus.BLOCKED;
  const isAgentAccessRestricted = isAgentOfflineInLonghorn || isLonghornBlocked;
  const isSidebarSwitchDisable = useSelector(
    (state: RootState) => state?.humanReminder?.isSidebarSwitchDisable
  );
  const isAmeyoErronous = useSelector((state: RootState) => state?.humanReminder?.isAmeyoErronous);
  const isChatAgentOnline = useSelector(
    (state: RootState) => state?.whatsappChatbot?.isAgentOnline
  );
  const isAgentWithInOperativeHours = useSelector(
    (state: RootState) => state?.common?.userData?.withinOperativeHours
  );

  const agentsData = useSelector((state: RootState) => state?.agentDocumentSlice?.documentsData);
  const documents = agentsData?.filter(
    doc => DOCUMENTS_MAP[doc.documentType as keyof typeof DOCUMENTS_MAP]
  );
  const isDraCertificatePresent = documents?.length > 0;
  const notifications = useSelector((store: RootState) => store?.notification.notifications);
  const agentEscalationCount = useSelector(
    (state: RootState) => state?.common?.agentEscalationCount
  );

  const lan = useSelector(
    (state: RootState) =>
      state?.common?.humanReminderCustomerDetails?.humanReminderResponse?.loanDetails
        ?.loanAccountNumber
  );
  const reminderRefId = useSelector(
    (state: RootState) =>
      state?.common?.humanReminderCustomerDetails?.humanReminderResponse?.reminderRefId
  );
  const campaignId = useSelector((state: RootState) => state?.humanReminder?.campaignId);

  const { isAmeyoNewIntegrationEnabled, isHRCNewIntegration } = useAmeyoIntegration();
  const disableAmeyoToolbarFlag = window?.config?.DISABLE_AMEYO_TOOLBAR === 'true' || isDc97User;

  const percentCompletedCases = useSelector(
    (state: RootState) => state.leaderboard.percentCompletedRecords
  )?.reduce(
    (total, current) => {
      return {
        paid: total.paid + current.paid,
        unpaid: total.unpaid + current.unpaid,
        total: total.paid + total.unpaid + current.paid + current.unpaid
      };
    },
    { paid: 0, unpaid: 0, total: 0 }
  );
  const completedCasesPercent = Number(
    ((percentCompletedCases.paid / percentCompletedCases.total) * 100).toFixed(0)
  );

  const isVendorPocEnabled = useSelector(
    (state: RootState) => state.common.featureFlags?.vendorPocEnabled || false
  );

  const currentRecommendedCase = useSelector(
    (state: RootState) => state?.caseRecommendation?.currentRecommendedCase
  );

  const isExternalAmeyoCallingEnabled = useSelector(
    (store: RootState) => store?.common?.featureFlags?.isExternalAmeyoCallingEnabled
  );

  const showFeedbackButton = !isDc97User && !isHRCChatUser;

  const shouldShowAmeyoToolbar = () => {
    if (isExternalAmeyoCallingEnabled) return true;
    if (disableAmeyoToolbarFlag) return false;
    if (isPerformanceDashboardVisible) return false;
    if (isDc97User) return false;
    if (isTeamLead) return false;
    if (isVendorPocEnabled) return false;
    if (!isNaviAgency(agencyCode || '')) return false;

    return true;
  };

  const showAmeyoCollapsibleToolbar = shouldShowAmeyoToolbar();

  const showTooltipCircularProgress =
    completedCasesPercent &&
    !isDc97User &&
    !isPerformanceDashboardVisible &&
    !isTeamLead &&
    !isOnlyGlobalAccessUser &&
    !isAgentOfflineInLonghorn &&
    !isLonghornBlocked;

  const disabledSidebarSwitch = isSidebarSwitchDisable || isAmeyoErronous;
  const checkedStateSidebarSwitch = isAgentOnline && !isSidebarSwitchDisable;
  const showHorizontalLine =
    countOfEscalationEnabled && isDraCertificatePresent && agentEscalationCount > 0;
  const showEscalationCount = countOfEscalationEnabled && agentEscalationCount > 0;

  const handleOnLogoutModalClick = () => {
    dispatch(setShowLogoutModal(!showLogoutModal));
    dispatch(getAgentDocuments());
    dispatch(getReporteesWithAllocation(agentReferenceId || '', countOfEscalationEnabled));
  };

  const toggleChatAgentOnlineHandler = () => {
    const statusToSet = isChatAgentOnline ? AGENT_STATUS.OFFLINE : AGENT_STATUS.ONLINE;
    dispatch(agentOnlineToggleHandler(statusToSet));
  };

  const linkClickHandler = (clickStreamEvent: { name: string; description: string }) => {
    addClickstreamEvent(clickStreamEvent);

    if (currentRecommendedCase) {
      dispatch(setRecommendedCases({ cases: [] }));
      dispatch(setCurrentRecommendedCase({ currentRecommendedCase: null }));
    }
  };

  const openFeedbackForm = () => {
    window.open(feedbackFormUrl, '_blank');
  };

  useEffect(() => {
    dispatch(getAgentDocuments());
  }, [agentId]);

  useEffect(() => {
    dispatch(getReporteesWithAllocation(agentReferenceId || '', countOfEscalationEnabled));
  }, [notifications]);

  const cleanupAndLogout = () => {
    clearParamsMap();
    dispatch(setAuthData({ token: null }));
    dispatch(setShowOTPScreen({ status: false }));
    dispatch(resetHumanReminderCustomerDetails(null));
    dispatch(resetState());
    dispatch(refreshCallData(null));
    dispatch(setManualLoginCreds({ email: '', password: '' }));
    localStorage.removeItem('persist:root');
    localStorage.removeItem(LOCAL_STORAGE_KEYS.LONGHORN_FIREBASE_TOKEN);
    localStorage.removeItem(HRC_CHAT_AGENT_ONLINE_STATUS);
    logOut();
  };

  const isImpersonation = useSelector(
    (state: RootState) => state?.common?.userData?.userImpersonated
  );

  const logOutHandler = async () => {
    if (userData?.roles?.includes(Roles.ROLE_HUMAN_REMINDER_AGENT)) {
      addClickstreamEvent(HRC_CLICKSTREAM.HRC_LOGOUT_CLICK, {
        agentRefId: userData?.referenceId,
        availabilityStatus: isAgentOnline,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus(),
        campaignId,
        reminderRefId
      });
    }
    if (isImpersonation) {
      try {
        await clearImageCache();
      } catch (error) {
        logError(error, 'Failed to clear image cache');
      }
    }
    if (isHRCChatUser) {
      isChatAgentOnline
        ? dispatch(
            agentOnlineToggleHandler(AGENT_STATUS.OFFLINE, () => {
              cleanupAndLogout();
            })
          )
        : cleanupAndLogout();
    } else {
      isDc97User && setAgentAvailability(AGENT_AVAILABILITY.AGENT_OFFLINE);
      sdkHelper.logoOut();
      ExtensionHandler.logout();
      ExtensionHandler.resetAllAmeyoData();
      isHRCNewIntegration ? agentOnlineHandler() : agentOnlineHandlerOld();
      cleanupAndLogout();
    }
  };

  useLocalStorageObserver(LOCAL_STORAGE_KEYS.LONGHORN_SESSION_TOKEN, (newValue: string | null) => {
    if (!newValue) {
      logOutHandler();
    }
  });

  const agentOnlineHandler = () => {
    addClickstreamEvent(AMEYO_SESSION_EVENTS.LH_AMEYO_AVAILABLE_TOGGLE_CLICKED, {
      loanAccountNumber: lan,
      phoneNumber: user?.phoneNumber,
      reminderRefId,
      campaignId,
      agentRefrenceId: userData?.referenceId,
      previousToggleStatus: isAgentOnline,
      newToggleStatus: !isAgentOnline,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
    if (sdkHelper.getAgentAvailability()) {
      sdkHelper.setOnBreak();
    } else {
      sdkHelper.setAvailable();
    }
    setAgentAvailability(
      !isAgentOnline ? AGENT_AVAILABILITY.AGENT_ONLINE : AGENT_AVAILABILITY.AGENT_OFFLINE
    );
  };

  const agentOnlineHandlerOld = () => {
    addClickstreamEvent(AMEYO_SESSION_EVENTS.LH_AMEYO_AVAILABLE_TOGGLE_CLICKED, {
      loanAccountNumber: lan,
      phoneNumber: user?.phoneNumber,
      reminderRefId,
      campaignId,
      agentRefrenceId: userData?.referenceId,
      previousToggleStatus: isAgentOnline,
      newToggleStatus: !isAgentOnline,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
    if (ExtensionHandler.currentAmeyoStatus() === AMEYO_STATUS_CODES.READY) {
      const clickStreamData = {
        reminderRefId,
        campaignId,
        agentRefrenceId: user?.referenceId
      };
      ExtensionHandler.setAvailability(!isAgentOnline, clickStreamData);
    } else {
      if (user?.email && user?.ameyoKey && user?.token && !isAmeyoNewIntegrationEnabled) {
        ExtensionHandler.login(user.email, user.ameyoKey);
      }
      toast('Ameyo is not ready yet', { type: 'error' });
    }
  };

  const isUserManagementRole =
    roles?.length === IT_TEAM_ROLES_COUNT &&
    requiredRolesForItTeam.every(role => roles?.includes(role));

  const getRedirectPath = () => {
    if (isAgentAccessRestricted) return '#';
    if (isUserManagementRole) return APP_ROUTES.ALL_AGENTS.path;
    if (isTeamLead) return APP_ROUTES.TEAM_LEAD_DASHBOARD.path;
    return '/';
  };

  return (
    <>
      <div
        className={styles.sideNavBarMain}
        onMouseLeave={() => {
          dispatch(setShowLogoutModal(false));
        }}
      >
        <div className={styles.logoContainer}>
          <Link
            to={getRedirectPath()}
            className={cx({ 'cursor-not-allowed': isAgentAccessRestricted })}
          >
            <NaviNewLogoIcon size={'md'} className={styles.naviLogo} />
          </Link>

          {showTooltipCircularProgress ? (
            <TooltipCircularProgress
              isVisible={showTooltipCircularProgress}
              percentCompletedCases={percentCompletedCases}
              completedCasesPercent={completedCasesPercent}
            />
          ) : null}
        </div>

        <div className={cx(styles.linksContainer, 'mt-4')}>
          <NavigationLinks
            items={SideBarItems}
            featureFlags={featureFlags}
            roles={roles}
            pathname={pathname}
            search={search}
            linkClickHandler={linkClickHandler}
          />
        </div>

        <div className={styles.bottomContainer}>
          {isDc97User ? (
            <div className={styles.switchContainer}>
              <Switch
                variant="secondary"
                checked={checkedStateSidebarSwitch}
                onChange={isHRCNewIntegration ? agentOnlineHandler : agentOnlineHandlerOld}
                disabled={disabledSidebarSwitch}
              ></Switch>
            </div>
          ) : null}
          {isHRCChatUser ? (
            <div className={styles.switchContainer}>
              <Switch
                variant="secondary"
                checked={isChatAgentOnline}
                onChange={toggleChatAgentOnlineHandler}
              ></Switch>
            </div>
          ) : null}

          {showAmeyoCollapsibleToolbar && !isHRCChatUser ? (
            <div
              className={cx(styles.ameyoToolbar, {
                '!cursor-not-allowed': isAgentAccessRestricted || !isAgentWithInOperativeHours
              })}
            >
              <AmeyoCollapsibleToolbarV4
                isCallHappening={isHappening => setDisableLogout(isHappening)}
              />
            </div>
          ) : null}

          {showFeedbackButton ? (
            <div className={cx(styles.bottomSideNavBarLinks, '!mt-0')} onClick={openFeedbackForm}>
              <FeedbackIcon />
              <Typography variant="h5" className={cx([styles.text, styles.showText])}>
                <span title={user?.name}>Feedback</span>
              </Typography>
            </div>
          ) : null}

          {breakTimeFeatureFlag && (
            <div className={cx(styles.bottomSideNavBarLinks)}>
              <OnlineOfflineSwitch />
            </div>
          )}

          <div className={styles.alignButton}>
            <LogoutModal
              showEscalationCount={showEscalationCount}
              agentEscalationCount={agentEscalationCount}
              showHorizontalLine={showHorizontalLine}
              disableLogout={disableLogout}
              logOutHandler={logOutHandler}
              isVisible={showLogoutModal}
            />

            <div
              className={cx(styles.bottomSideNavBarLinks, styles.logoutLink)}
              onClick={handleOnLogoutModalClick}
            >
              {showEscalationCount ? <EscalationUserIcon /> : <AgentIcon />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideNavigationPanel;
